import React, { useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import "../css/cookie-responsive.css";
import {
  ShieldCheck, Search, CalendarDays, ChevronDown, CheckCircle2, AlertTriangle,
  BarChart3, List as ListIcon, LayoutGrid, Globe, RefreshCw, Download, Plus,
  Users, Cookie, Monitor, MoreVertical, Smartphone, X,
} from "lucide-react";
import CommonPagination from "./CommonPagination";

/* ---------- Design tokens ---------- */
const C = {
  ink: "#0f172a", sub: "#64748b", faint: "#94a3b8", line: "#e2e8f0",
  bg: "#f8fafc", white: "#ffffff", blue: "#2563eb",
  green: "#059669", greenBg: "#ecfdf5", greenTx: "#047857",
  amber: "#d97706", amberBg: "#fffbeb", amberTx: "#b45309",
  red: "#dc2626", teal: "#0d9488",
};

const btn = (bg, color = "#fff", border = "none") => ({
  height: 38, padding: "0 14px", borderRadius: 8, border, background: bg, color,
  fontSize: 12.5, fontWeight: 600, display: "inline-flex", alignItems: "center",
  gap: 6, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
});

/* ---------- Page Guide modal ---------- */
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflowY: "auto" }}
    >
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 620, border: `1px solid ${C.line}`, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink }}>📖 Page Guide</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.faint }}><X size={18} /></button>
        </div>
        <div style={{ padding: "18px 20px", maxHeight: "75vh", overflowY: "auto" }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: i < steps.length - 1 ? "1px solid #f1f5f9" : "none" }}>
              <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: "#eff6ff", color: C.blue, fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: "#4b5563", lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Browser Brand Icons */
const ChromeIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className}>
    <path fill="#4CAF50" d="M24 44c8.3 0 15.5-5.1 18.5-12.4l-10-17.3c-.9 1.5-2.2 2.8-3.8 3.7l-9.3 16.1c1.5.6 3.1.9 4.6.9z" />
    <path fill="#FFC107" d="M44 24c0-2.3-.4-4.5-1.1-6.5l-20.1.1c1.7.3 3.3 1.1 4.5 2.3l11.4 19.7C42.2 35.5 44 30 44 24z" />
    <path fill="#FF3D00" d="M24 4C14.7 4 6.8 9.9 4 18.1l10.3 17.8c1.3-1.2 2.8-2.1 4.5-2.6L24 16h18.9C39.5 8.9 32.3 4 24 4z" />
    <circle cx="24" cy="24" r="10" fill="#FFFFFF" />
    <circle cx="24" cy="24" r="8" fill="#1976D2" />
  </svg>
);

const EdgeIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="none">
    <defs>
      <linearGradient id="edge_cnp_a" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0C59A4" />
        <stop offset="100%" stopColor="#114A8B" />
      </linearGradient>
      <linearGradient id="edge_cnp_b" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#0078D7" />
        <stop offset="60%" stopColor="#00BCF2" />
        <stop offset="100%" stopColor="#2CD889" />
      </linearGradient>
    </defs>
    <path d="M22 12c0 5.52-4.48 10-10 10A10 10 0 0 1 2 12C2 6.48 6.48 2 12 2c.28 0 .5.22.5.5 0 2-1 4.5-3.5 5.5-2.2 1-3.5 2.5-3.5 4 0 3.04 2.46 5.5 5.5 5.5 2.8 0 5-2 5.5-4.5.1-.5.5-.9 1-.9H21.5c.28 0 .5.22.5.5V12z" fill="url(#edge_cnp_a)" />
    <path d="M12 22c5.52 0 10-4.48 10-10 0-4.5-3-8.5-7.5-9.5-.3 0-.5.3-.5.6 0 2-1 4.5-3 6-1.8 1.4-2.5 3.4-2 5.4.8 3.5 3.8 7.5 3 7.5z" fill="url(#edge_cnp_b)" />
  </svg>
);

const FirefoxIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <defs>
      <linearGradient id="ff_cnp_bg" x1="4" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FF9400" />
        <stop offset="45%" stopColor="#FF2A54" />
        <stop offset="100%" stopColor="#7622FF" />
      </linearGradient>
      <linearGradient id="ff_cnp_flame" x1="12" y1="12" x2="36" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFE600" />
        <stop offset="100%" stopColor="#FF6600" />
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="20" fill="url(#ff_cnp_bg)" />
    <path d="M33 16c-3-3-7.5-4.5-12-4.5-6.5 0-12 5.5-12 12 0 2.3.6 4.4 1.7 6.3 3-3 5-7.3 5-12 0-1-.1-2-.4-3 3.5 2.5 6 6.5 6 11 0 4-1.8 7.5-4.5 10 3.8 2 8.2 3.1 12.8 3.1 9.2 0 16.7-7.5 16.7-16.7 0-3.3-1-6.4-2.7-9-1.6 3-4.6 5.1-8 5.5-1.3-2.4-2.5-5.3-2.6-8.7z" fill="url(#ff_cnp_flame)" />
  </svg>
);

const SafariIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <defs>
      <linearGradient id="saf_cnp_bg" x1="24" y1="4" x2="24" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#2BA6FF" />
        <stop offset="100%" stopColor="#0062DF" />
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="20" fill="url(#saf_cnp_bg)" stroke="#FFFFFF" strokeWidth="2" />
    <polygon points="24,10 28,24 24,38 20,24" fill="#FF3B30" />
    <polygon points="24,24 28,24 24,38 20,24" fill="#FFFFFF" />
    <polygon points="24,10 20,24 24,24" fill="#D32F2F" />
    <polygon points="24,24 20,24 24,38" fill="#D1D5DB" />
    <circle cx="24" cy="24" r="2.5" fill="#FFFFFF" />
  </svg>
);

const OperaIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <path d="M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4zm0 6.5c6 0 10 6 10 13.5s-4 13.5-10 13.5-10-6-10-13.5 4-13.5 10-13.5z" fill="#FF1B2D" />
  </svg>
);

const BraveIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <path d="M24 4L6 11v11c0 10.5 7.6 18 18 22 10.4-4 18-11.5 18-22V11L24 4zm0 7l12 5v7c0 7-5 12.6-12 15.6-7-3-12-8.6-12-15.6v-7l12-5z" fill="#FB542B" />
    <path d="M24 16l8 3.5v4.5c0 5-3.5 9-8 11.2-4.5-2.2-8-6.2-8-11.2V24l8-8z" fill="#FF7A00" opacity="0.6" />
  </svg>
);

const SamsungIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <circle cx="24" cy="24" r="16" fill="#1C69D4" />
    <ellipse cx="24" cy="24" rx="20" ry="8" fill="none" stroke="#7C3AED" strokeWidth="3.5" transform="rotate(-30 24 24)" />
  </svg>
);

const getBrowserComponent = (name = "") => {
  const n = String(name || "").toLowerCase().trim();
  if (n.includes("edge") || n.includes("edg")) return EdgeIcon;
  if (n.includes("chrome") || n.includes("crios")) return ChromeIcon;
  if (n.includes("firefox") || n.includes("fxios")) return FirefoxIcon;
  if (n.includes("safari")) return SafariIcon;
  if (n.includes("opera") || n.includes("opr")) return OperaIcon;
  if (n.includes("brave")) return BraveIcon;
  if (n.includes("samsung")) return SamsungIcon;
  return null;
};

/* ---------- Small helpers ---------- */
const getHost = (url, fallback = "unknown") => {
  try { return new URL(url).hostname || fallback; } catch (e) { return fallback; }
};

const getRecordIp = (r) => r.ip || r.ip_address || r.remote_addr || r.source_ip || r.remoteIp || "";
const getRecordUA = (r) => r.user_agent || r.ua || r.userAgent || r.browser || "";

// Browser pehle, phir OS. Example: "Edge 153 • Windows 10/11"
const parseUA = (ua = "") => {
  const s = String(ua || "").trim();
  if (!s) return { label: "Unknown browser", mobile: false, browser: "", browserName: "", os: "" };

  let os = "";
  if (/Windows NT 10/i.test(s)) os = "Windows 10/11";
  else if (/Windows/i.test(s)) os = "Windows";
  else if (/Android/i.test(s)) os = "Android";
  else if (/iPhone|iPad|iOS/i.test(s)) os = "iOS";
  else if (/Mac OS X|Macintosh/i.test(s)) os = "MacOS";
  else if (/Linux/i.test(s)) os = "Linux";

  // Order matters: Edge/Opera/Samsung UA mein "Chrome" aur "Safari" bhi hota hai
  const rules = [
    ["Edge", /(?:Edg|EdgA|EdgiOS|Edge)[\/ ]([\d.]+)/],
    ["Opera", /(?:OPR|Opera)[\/ ]([\d.]+)/],
    ["Samsung Internet", /SamsungBrowser[\/ ]([\d.]+)/],
    ["Firefox", /(?:Firefox|FxiOS)[\/ ]([\d.]+)/],
    ["Chrome", /(?:Chrome|CriOS)[\/ ]([\d.]+)/],
    ["Safari", /Version[\/ ]([\d.]+).*Safari/],
  ];
  let browser = "";
  let browserName = "";
  for (const [name, re] of rules) {
    const m = s.match(re);
    if (m) {
      browserName = name;
      browser = `${name} ${m[1].split(".")[0]}`;
      break;
    }
  }
  // Fallback: field mein sirf plain naam ho, jaise "Chrome" / "Brave"
  if (!browser) {
    const m = s.match(/edge|opera|firefox|chrome|safari|brave/i);
    if (m) {
      browserName = m[0].charAt(0).toUpperCase() + m[0].slice(1).toLowerCase();
      browser = browserName;
    }
  }

  return {
    label: [browser || "Unknown browser", os].filter(Boolean).join(" • "),
    mobile: /Android|iPhone|iPad|iOS/i.test(s),
    browser,
    browserName,
    os,
  };
};

const parsePrefs = (prefs) => {
  if (!prefs) return {};
  if (typeof prefs === "string") {
    try { prefs = JSON.parse(prefs); } catch (e) { return {}; }
  }
  return typeof prefs === "object" ? prefs : {};
};

const parseDate = (s) => {
  if (!s) return null;
  try {
    const str = String(s).trim();
    const isoNaive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
    const d = isoNaive.test(str) ? new Date(str.replace(" ", "T") + "Z") : new Date(str);
    return isNaN(d.getTime()) ? null : d;
  } catch (e) { return null; }
};

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata", hour12: false,
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).format(d).replace(",", ",");
  } catch (e) { return d.toString(); }
};

const getExpires = (r) => {
  if (r.expires_at) {
    const e = parseDate(r.expires_at);
    if (e) return e;
  }
  const created = parseDate(r.created_at);
  return created ? new Date(created.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
};

const isExpired = (r) => {
  const e = getExpires(r);
  return e ? new Date() > e : false;
};

const isVariance = (r) => /varian/i.test(String(r.consent_status || r.status || ""));

// Row state shown in the Status column
const getRowState = (r) => {
  if (isVariance(r)) return "variance";
  if (isExpired(r)) return "expired";
  return "active";
};

const consentChip = (status = "") => {
  if (/accept/i.test(status)) return { bg: C.greenBg, tx: C.greenTx, icon: <CheckCircle2 size={14} />, label: "Accepted" };
  if (/varian/i.test(status)) return { bg: C.amberBg, tx: C.amberTx, icon: <AlertTriangle size={14} />, label: "Variance" };
  if (/revok/i.test(status)) return { bg: "#fff7ed", tx: "#c2410c", icon: <AlertTriangle size={14} />, label: "Revoked" };
  if (/declin/i.test(status)) return { bg: "#fef2f2", tx: "#b91c1c", icon: <X size={14} />, label: "Declined" };
  return { bg: "#f1f5f9", tx: C.sub, icon: null, label: status || "N/A" };
};

const STATE_STYLE = {
  active: { bg: C.greenBg, tx: C.greenTx, label: "Active", dot: C.green },
  expired: { bg: "#fef2f2", tx: "#b91c1c", label: "Expired", dot: C.red },
  variance: { bg: C.amberBg, tx: C.amberTx, label: "Variance", dot: C.amber },
};

const DATE_RANGES = [
  { v: "all", label: "Select date range", days: null },
  { v: "today", label: "Today", days: 1 },
  { v: "7", label: "Last 7 days", days: 7 },
  { v: "30", label: "Last 30 days", days: 30 },
  { v: "90", label: "Last 90 days", days: 90 },
];

/* ---------- Small UI pieces ---------- */
function Chip({ children, tone = "green" }) {
  const t = tone === "green"
    ? { bg: "#ecfdf5", tx: "#047857" }
    : { bg: "#f1f5f9", tx: C.sub };
  return (
    <span style={{ background: t.bg, color: t.tx, padding: "3px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function PrefChips({ prefs }) {
  const entries = Object.entries(parsePrefs(prefs));
  if (!entries.length) return <span style={{ color: C.faint, fontSize: 12 }}>—</span>;
  const on = entries.filter(([, v]) => v);
  const shown = on.slice(0, 2);
  const extra = on.length - shown.length;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {shown.map(([k]) => <Chip key={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</Chip>)}
      {extra > 0 && <Chip tone="grey">+{extra}</Chip>}
      {!on.length && <Chip tone="grey">None</Chip>}
    </div>
  );
}

function StatCard({ tone, icon, sideIcon, label, value, hint }) {
  const tones = {
    green: { bg: "#f0fdf4", br: "#bbf7d0", ic: C.green },
    blue: { bg: "#eff6ff", br: "#bfdbfe", ic: C.blue },
    amber: { bg: "#fff7ed", br: "#fed7aa", ic: C.amber },
  }[tone];
  return (
    <div style={{ minWidth: 0, background: tones.bg, border: `1px solid ${tones.br}`, borderRadius: 14, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", color: tones.ic, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 12, color: C.sub }}>{hint}</div>
      </div>
      <div style={{ color: tones.ic, opacity: 0.8 }}>{sideIcon}</div>
    </div>
  );
}

function RowMenu({ r }) {
  const [open, setOpen] = useState(false);
  const ip = getRecordIp(r);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ background: "none", border: "none", cursor: "pointer", color: C.ink, padding: 6 }} aria-label="Row actions">
        <MoreVertical size={16} />
      </button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: 30, zIndex: 20, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,.12)", minWidth: 150, overflow: "hidden" }}>
          {r.source_url && (
            <a href={r.source_url} target="_blank" rel="noreferrer" style={{ display: "block", padding: "9px 12px", fontSize: 12.5, color: C.ink, textDecoration: "none" }}>Open website</a>
          )}
          {ip && (
            <button onMouseDown={() => navigator.clipboard && navigator.clipboard.writeText(ip)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", fontSize: 12.5, color: C.ink, background: "none", border: "none", cursor: "pointer" }}>
              Copy IP address
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ================= MAIN PAGE ================= */
function CookiesNotifyPage({ user }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [view, setView] = useState("list"); // "list" = table, "grid" = cards
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [webConsentActive, setWebConsentActive] = useState(false);
  const [siteFilter, setSiteFilter] = useState(null);
  const [consentStatsLoading, setConsentStatsLoading] = useState(false);
  const [consentStatsResult, setConsentStatsResult] = useState(null);
  const [cookiesStatsLoading, setCookiesStatsLoading] = useState(false);
  const [cookiesStatsResult, setCookiesStatsResult] = useState(null);
  const [consentVarianceLoading, setConsentVarianceLoading] = useState(false);
  const [consentVarianceResult, setConsentVarianceResult] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const GUIDE_STEPS = [
    { title: "Cookie Consent Tracker", body: "View and manage all cookie consent events recorded across your applications." },
    { title: "Summary cards", body: "Total consents, cookie types tracked, and how many records have a variance." },
    { title: "Search & date range", body: "Search by app, domain, IP, user agent or consent type, and narrow results by date." },
    { title: "Consent / Variance / Cookies", body: "Push consent stats, consent variance or cookie stats to the external OneDPDP dashboard." },
    { title: "List / Grid", body: "Switch between the table and card layouts." },
    { title: "Web App", body: "Show only web-app (Portal) consent events and create new consent requests." },
    { title: "Export", body: "Download the records you see on screen as a CSV file." },
    { title: "Add Consent", body: "Open the web-app consent form to record a new consent." },
  ];

  const getTenantId = () =>
    (window.USER && window.USER.tenant_id) ? Number(window.USER.tenant_id) : (Number(localStorage.getItem("tenant_id")) || null);

  /* ----- Load data ----- */
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = getTenantId();
      let url = `${API_BASE}/user/cookie-consents?page=${page}&page_size=${perPage}`;
      if (tenantId) url += `&tenant_id=${encodeURIComponent(tenantId)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (siteFilter) url += `&site=${encodeURIComponent(siteFilter)}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.cookie_consents || []);
      setRecords(rows || []);
      setTotalItems(data.total || rows.length || 0);
      setTotalPages(data.total_pages || Math.ceil((data.total || rows.length || 0) / perPage) || 1);
    } catch (e) {
      setError("Failed to load cookie consents");
      setRecords([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, perPage, search, siteFilter]);

  /* ----- Client-side filters ----- */
  const displayRecords = useMemo(() => {
    const text = search.toLowerCase();
    const range = DATE_RANGES.find((d) => d.v === dateRange);
    const since = range && range.days ? Date.now() - range.days * 86400000 : null;

    return records.filter((r) => {
      if (siteFilter) {
        const src = (r.source_url || "").toString().toLowerCase();
        const host = getHost(src, "");
        const app = (r.app_name || "").toString().toLowerCase();
        const f = siteFilter.toLowerCase();
        if (!(src.includes(f) || host === f || app === f)) return false;
      }
      if (webConsentActive) {
        const s = ((r.consent_status || r.status || r.consent) || "").toString().toLowerCase();
        if (!/accept|declin|revok/.test(s)) return false;
      }
      if (since) {
        const d = parseDate(r.created_at);
        if (!d || d.getTime() < since) return false;
      }
      return (
        (r.app_name || "").toLowerCase().includes(text) ||
        (r.consent_status || "").toLowerCase().includes(text) ||
        (r.source_url || "").toLowerCase().includes(text) ||
        getRecordIp(r).toLowerCase().includes(text) ||
        getRecordUA(r).toLowerCase().includes(text)
      );
    });
  }, [records, search, siteFilter, webConsentActive, dateRange]);

  /* ----- Summary numbers ----- */
  const stats = useMemo(() => {
    const types = new Set();
    let variance = 0;
    records.forEach((r) => {
      Object.keys(parsePrefs(r.preferences)).forEach((k) => types.add(k));
      if (isVariance(r)) variance += 1;
    });
    return { total: totalItems || records.length, cookieTypes: types.size, variance, typeCount: types.size };
  }, [records, totalItems]);

  /* ----- Export CSV ----- */
  const handleExport = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["App", "URL", "IP", "User Agent", "Created", "Expires", "Consent Status", "Preferences"];
    const rows = displayRecords.map((r) => [
      r.app_name, r.source_url, getRecordIp(r), getRecordUA(r),
      formatDate(parseDate(r.created_at)), formatDate(getExpires(r)),
      r.consent_status, JSON.stringify(parsePrefs(r.preferences)),
    ].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cookie-consents-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /* ----- Add consent (opens web-app consent form) ----- */
  const handleAddConsent = () => {
    const baseUrl = window.location.origin;
    const tenantId = user?.tenant_id || user?.tenantId || localStorage.getItem("tenant_id") || "1";
    const popup = window.open(`${baseUrl}/webapp-consent?app=Portal`, "_blank");
    const initPayload = { type: "INIT_CONSENT", email: user?.email || "", tenant_id: tenantId, force: 1, redirect: baseUrl };
    try { if (popup && !popup.closed) popup.postMessage(initPayload, "*"); } catch (e) { /* ignore */ }
    const iv = setInterval(() => {
      try { if (popup && !popup.closed) popup.postMessage(initPayload, "*"); } catch (e) { /* ignore */ }
    }, 400);
    setTimeout(() => clearInterval(iv), 4000);
  };

  /* ----- Push handlers (logic unchanged) ----- */
  const handlePushConsentStats = async () => {
    setConsentStatsLoading(true);
    setConsentStatsResult(null);
    const token = localStorage.getItem("token");
    try {
      const statsRes = await fetch(`${API_BASE}/manager/consent-stats-data`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!statsRes.ok) throw new Error("Failed to fetch stats data");
      const statsData = await statsRes.json();

      const pushRes = await fetch(`${API_BASE}/manager/push-consent-stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(statsData),
      });
      const pushResult = await pushRes.json();
      if (pushRes.ok && pushResult.status === "success") {
        setConsentStatsResult("Consent stats pushed successfully!");
      } else {
        setConsentStatsResult("Failed to push consent stats: " + (pushResult.detail || "Unknown error"));
      }
    } catch (e) {
      setConsentStatsResult("Error: " + (e.message || e.toString()));
    } finally {
      setConsentStatsLoading(false);
    }
  };

  const handlePushCookiesStats = async () => {
    setCookiesStatsLoading(true);
    setCookiesStatsResult(null);
    const token = localStorage.getItem("token");
    const tenantId = getTenantId();
    if (!tenantId) {
      setCookiesStatsResult("Tenant id not available");
      setCookiesStatsLoading(false);
      return;
    }
    try {
      try {
        await fetch(`${API_BASE}/manager/cookies-stats-data`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ organization_id: tenantId }),
        });
      } catch (e) {
        console.warn("cookies-stats-data save failed (non-fatal):", e);
      }

      const pushRes = await fetch(`${API_BASE}/manager/push-cookies-stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ organization_id: tenantId }),
      });
      const pushJson = await pushRes.json().catch(() => ({}));

      if (pushRes.ok && (pushJson.status === "success" || pushJson.external_status === 200)) {
        setCookiesStatsResult("Cookies stats pushed successfully!");
      } else {
        let msg = "";
        const d = pushJson.detail;
        if (d && typeof d === "object") {
          const body = typeof d.external_body === "string" ? d.external_body : JSON.stringify(d.external_body);
          msg = `${d.message || "Upstream error"} (status ${d.external_status}): ${body}`;
        } else if (typeof d === "string") {
          msg = d;
        } else {
          msg = JSON.stringify(pushJson) || String(pushRes.status);
        }
        console.error("push-cookies-stats failed:", pushJson);
        setCookiesStatsResult("Failed to push cookies stats: " + msg);
      }
    } catch (e) {
      setCookiesStatsResult("Error: " + (e.message || e.toString()));
    } finally {
      setCookiesStatsLoading(false);
    }
  };

  const handlePushConsentVariance = async () => {
    setConsentVarianceLoading(true);
    setConsentVarianceResult(null);
    const token = localStorage.getItem("token");
    const tenantId = getTenantId();
    if (!tenantId) {
      setConsentVarianceResult("Tenant id not available");
      setConsentVarianceLoading(false);
      return;
    }
    try {
      const consentRes = await fetch(`${API_BASE}/user/consent?tenant_id=${encodeURIComponent(tenantId)}&limit=10000`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!consentRes.ok) throw new Error("Failed to fetch consent records");
      const consentJson = await consentRes.json();
      const recs = Array.isArray(consentJson) ? consentJson : Array.isArray(consentJson?.consents) ? consentJson.consents : [];
      if (!recs.length) {
        setConsentVarianceResult("No consent records found for tenant");
        setConsentVarianceLoading(false);
        return;
      }
      const validRecords = recs.filter((r) => r.type && r.entity && r.email && r.id_proof_type);
      if (!validRecords.length) {
        setConsentVarianceResult("No valid consent records found with type, entity, email and data_type_consent");
        setConsentVarianceLoading(false);
        return;
      }

      const results = [];
      for (const rec of validRecords) {
        try {
          const rawDataTypes = rec.data_type_consent || rec.dataTypeConsent || rec.Data_Type_consent || "";
          const dataTypes = Array.isArray(rawDataTypes)
            ? rawDataTypes
            : String(rawDataTypes).split(",").map((v) => v.trim()).filter(Boolean);
          const payload = {
            organisation_id: tenantId,
            module: "CONSENT",
            consent_type: rec.type || "Unknown",
            consent_source: rec.entity || "Unknown",
            "data owner": rec.email || "Unknown",
            "data types": dataTypes.length ? dataTypes : ["Unknown"],
            data_type_consent: Array.isArray(rawDataTypes) ? rawDataTypes.join(",") : String(rawDataTypes || "").trim(),
            consent_status: rec.consent_status || rec.status || rec.consent || "granted",
            consent_purpose: rec.consent || rec.type || "Consent variance",
            request_source: rec.entity || rec.source_url || rec.app_name || "Portal",
          };
          const res = await fetch(`${API_BASE}/user/consent-variance`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(payload),
          });
          const j = await res.json().catch(() => ({}));
          if (res.ok && j.status === "success") results.push({ recordId: rec.id, ok: true });
          else results.push({ recordId: rec.id, ok: false, detail: j.detail || JSON.stringify(j) || String(res.status) });
        } catch (e) {
          results.push({ recordId: rec.id, ok: false, detail: e.message || String(e) });
        }
      }
      const successCount = results.filter((r) => r.ok).length;
      setConsentVarianceResult(`Pushed ${successCount}/${results.length} consent-variance entries` + (successCount === results.length ? " — success" : " — partial/failure"));
    } catch (e) {
      setConsentVarianceResult("Error: " + (e.message || String(e)));
    } finally {
      setConsentVarianceLoading(false);
    }
  };

  /* ----- Shared cell renderers ----- */
  const AppIcon = ({ r }) => {
    const host = getHost(r.source_url, r.app_name || "");
    const local = /localhost|127\.0\.0\.1/.test(host || r.source_url || "");
    const ua = parseUA(getRecordUA(r));
    const BrowserComp = getBrowserComponent(ua.browserName || ua.browser);

    if (BrowserComp) {
      return (
        <div
          title={ua.label || ua.browserName}
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: "#f8fafc",
            border: `1px solid ${C.line}`,
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <BrowserComp size={20} />
        </div>
      );
    }

    return (
      <div
        title={local ? "Localhost" : "Web"}
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: local ? "#f97316" : C.blue,
          color: "#fff",
        }}
      >
        {local ? <Monitor size={18} /> : <Globe size={18} />}
      </div>
    );
  };

  const resultMsg = (msg, okText) =>
    msg ? (
      <span style={{ fontSize: 12, fontWeight: 600, color: String(msg).toLowerCase().includes(okText) ? C.green : C.red }}>{msg}</span>
    ) : null;

  const th = { padding: "14px 16px", textAlign: "left", fontSize: 12.5, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", background: "#f8fafc", borderBottom: `1px solid ${C.line}` };
  const td = { padding: "14px 16px", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9", fontSize: 12.5, color: C.ink };

  /* ================= RENDER ================= */
  return (
    <div style={{ padding: 20, background: C.bg, minHeight: "100vh", fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif" }}>

      {/* ---------- HEADER (2 rows: identity + search / toolbar) ---------- */}
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, marginBottom: 16, overflow: "hidden" }}>

        {/* Row 1: brand on the left, search + date on the right */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#eff6ff", color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ShieldCheck size={26} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em", whiteSpace: "nowrap" }}>Cookie Consent Tracker</h2>
              <div style={{ fontSize: 12.5, color: C.sub, marginTop: 2 }}>View and manage user cookie consent details and preferences</div>
            </div>

          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flex: "1 1 420px", justifyContent: "flex-end" }}>
            <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 420, minWidth: 220 }}>
              <Search size={15} color={C.faint} style={{ position: "absolute", left: 12, top: 12 }} />
              <input
                className="tour-target-cookie-search"
                type="text"
                placeholder="Search by app, domain, IP, user agent, or consent type..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ width: "100%", height: 38, padding: "0 12px 0 34px", borderRadius: 8, border: `1px solid ${C.line}`, fontSize: 12.5, color: C.ink, outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <CalendarDays size={15} color={C.sub} style={{ position: "absolute", left: 12, top: 12, pointerEvents: "none" }} />
              <ChevronDown size={15} color={C.ink} style={{ position: "absolute", right: 12, top: 12, pointerEvents: "none" }} />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{ height: 38, minWidth: 190, padding: "0 34px", borderRadius: 8, border: `1px solid ${C.line}`, background: "#fff", fontSize: 12.5, color: C.ink, appearance: "none", cursor: "pointer" }}
              >
                {DATE_RANGES.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
              </select>
            </div>
            <button onClick={() => setGuideOpen(true)} style={{ ...btn("#fff", "#334155", "1px solid #cbd5e1"), height: 34, padding: "0 12px" }}>
              📖 Page Guide
            </button>
          </div>
        </div>

        {/* Row 2: push actions (left) and view controls (right) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "12px 20px", borderTop: `1px solid ${C.line}`, background: "#fafbfd" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: C.sub, fontWeight: 600, marginRight: 4 }}>Push to OneDPDP</span>
            <button className="tour-target-push-consent" onClick={handlePushConsentStats} disabled={consentStatsLoading}
              style={{ ...btn(C.green), opacity: consentStatsLoading ? 0.6 : 1 }}>
              <CheckCircle2 size={15} /> {consentStatsLoading ? "Pushing..." : "Consent"}
            </button>
            <button className="tour-target-push-variance" onClick={handlePushConsentVariance} disabled={consentVarianceLoading}
              style={{ ...btn("#f59e0b"), opacity: consentVarianceLoading ? 0.6 : 1 }}>
              <AlertTriangle size={15} /> {consentVarianceLoading ? "Pushing..." : "Variance"}
            </button>
            <button className="tour-target-push-cookies" onClick={handlePushCookiesStats} disabled={cookiesStatsLoading}
              style={{ ...btn(C.blue), opacity: cookiesStatsLoading ? 0.6 : 1 }}>
              <BarChart3 size={15} /> {cookiesStatsLoading ? "Pushing..." : "Cookies"}
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button className="tour-target-view-toggle" onClick={() => setView(view === "list" ? "grid" : "list")}
              style={btn("#fff", "#334155", "1px solid #cbd5e1")}>
              {view === "list" ? <><LayoutGrid size={15} /> Grid</> : <><ListIcon size={15} /> List</>}
            </button>
            {/* NOTE: no "icon-btn webapp" class here - cookie-responsive.css was clipping this button */}
            <button className="tour-target-webapp" title="Web App Consent"
              onClick={() => { setWebConsentActive(true); setSearch("portal"); setPage(1); }}
              style={{ ...btn(C.teal), width: "auto", minWidth: "max-content", overflow: "visible" }}>
              <Globe size={15} /> <span>Web App</span>
            </button>
            <button className="tour-target-cookie-refresh" onClick={load} title="Refresh"
              style={btn("#fff", C.blue, "1px solid #cbd5e1")}>
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* push results */}
      {(consentStatsResult || cookiesStatsResult || consentVarianceResult) && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
          {resultMsg(consentStatsResult, "pushed successfully")}
          {resultMsg(cookiesStatsResult, "pushed successfully")}
          {resultMsg(consentVarianceResult, "success")}
        </div>
      )}

      {/* ---------- SUMMARY CARDS (equal width grid) ---------- */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 20 }}>
        <StatCard tone="green" icon={<CheckCircle2 size={22} />} sideIcon={<Users size={30} />}
          label="Total Consents" value={stats.total.toLocaleString("en-IN")} hint="Across all applications" />
        <StatCard tone="blue" icon={<Cookie size={22} />} sideIcon={<Cookie size={30} />}
          label="Active Cookies" value={stats.cookieTypes} hint="Total cookie types tracked" />
        <StatCard tone="amber" icon={<AlertTriangle size={22} />} sideIcon={<AlertTriangle size={30} />}
          label="Variance Found" value={stats.variance} hint="Requires attention" />
      </div>

      {/* ---------- TABLE TOOLBAR: title + Export / Add Consent ---------- */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
          Consent records
          <span style={{ marginLeft: 8, fontSize: 12.5, fontWeight: 500, color: C.sub }}>{displayRecords.length} shown</span>
        </div>
        {/* <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleExport} style={btn("#fff", C.blue, "1px solid #cbd5e1")}><Download size={15} /> Export</button>
          <button onClick={handleAddConsent} style={btn(C.blue)}><Plus size={15} /> Add Consent</button>
        </div> */}
      </div>

      {/* ---------- WEB APP QUICK BAR ---------- */}
      {webConsentActive && (
        <div style={{ marginBottom: 16, padding: 12, background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 12, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 13, color: "#1e40af", marginBottom: 8 }}>
              Showing web-app consent events (Portal). Pick a site to narrow the list.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => { setSiteFilter(null); setSearch(""); setPage(1); }}
                style={{ ...btn(!siteFilter ? C.blue : "#fff", !siteFilter ? "#fff" : C.ink, "1px solid #cbd5e1"), height: 30 }}>
                All sites
              </button>
              {(() => {
                const map = {};
                records.forEach((rr) => {
                  const key = getHost(rr.source_url, (rr.app_name || "").toString()) || "unknown";
                  map[key] = (map[key] || 0) + 1;
                });
                return Object.keys(map).sort((a, b) => map[b] - map[a]).slice(0, 50).map((k) => (
                  <button key={k} onClick={() => { setSiteFilter(k); setSearch(""); setPage(1); }}
                    style={{ ...btn(siteFilter === k ? C.blue : "#fff", siteFilter === k ? "#fff" : C.ink, "1px solid #cbd5e1"), height: 30 }}>
                    {k} ({map[k]})
                  </button>
                ));
              })()}
            </div>
          </div>
          <button onClick={() => { setWebConsentActive(false); setSiteFilter(null); setSearch(""); }}
            style={btn("#fff", C.ink, "1px solid #cbd5e1")}>Close</button>
        </div>
      )}

      {error && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {loading && <div style={{ color: C.sub, fontSize: 13, marginBottom: 12 }}>Loading...</div>}

      {/* ---------- LIST (TABLE) VIEW ---------- */}
      {view === "list" && (
        <div className="tour-target-cookie-cards" style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 48 }}>#</th>
                  <th style={th}>Domain / Application</th>
                  <th style={th}>User / IP Address</th>
                  <th style={th}>Consent Date &amp; Time</th>
                  <th style={th}>Consent Type</th>
                  <th style={th}>Cookies Accepted</th>
                  <th style={th}>Preferences</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRecords.map((r, i) => {
                  const prefs = parsePrefs(r.preferences);
                  const keys = Object.keys(prefs);
                  const acceptedCount = keys.filter((k) => prefs[k]).length;
                  const ua = parseUA(getRecordUA(r));
                  const chip = consentChip(r.consent_status);
                  const state = getRowState(r);
                  const ss = STATE_STYLE[state];
                  const expires = getExpires(r);
                  const expired = isExpired(r);
                  const host = getHost(r.source_url, r.app_name || "Unknown App");
                  return (
                    <tr key={r.id || i}>
                      <td style={{ ...td, fontWeight: 700 }}>{(page - 1) * perPage + i + 1}</td>

                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <AppIcon r={r} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700 }}>{r.app_name || host}</div>
                            {r.source_url && (
                              <a href={r.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: C.sub, textDecoration: "none", wordBreak: "break-all" }}>
                                {r.source_url}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#334155" }}>
                          <Users size={14} color={C.sub} /> {getRecordIp(r) || "—"}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.sub, marginTop: 4, fontSize: 11.5 }}>
                          {ua.mobile ? <Smartphone size={14} /> : <Monitor size={14} />}
                          {ua.label}
                        </div>
                      </td>

                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <CalendarDays size={14} color={C.sub} /> {formatDate(parseDate(r.created_at)) || "—"}
                        </div>
                        {expires && (
                          <div style={{ marginTop: 4, fontSize: 11.5, color: expired ? C.red : C.sub, marginLeft: 22 }}>
                            Expires: {formatDate(expires)}
                          </div>
                        )}
                      </td>

                      <td style={td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, background: chip.bg, color: chip.tx, fontWeight: 600 }}>
                          {chip.icon} {chip.label}
                        </span>
                      </td>

                      <td style={{ ...td, fontWeight: 600 }}>
                        {keys.length ? `${acceptedCount} / ${keys.length}` : "—"}
                      </td>

                      <td style={td}><PrefChips prefs={r.preferences} /></td>

                      <td style={td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, background: ss.bg, color: ss.tx, fontWeight: 600 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: ss.dot }} /> {ss.label}
                        </span>
                      </td>

                      <td style={{ ...td, textAlign: "center" }}><RowMenu r={r} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "10px 16px", fontSize: 12, color: C.sub }}>
            Showing {displayRecords.length ? 1 : 0} - {displayRecords.length} of {displayRecords.length} entries
          </div>
        </div>
      )}

      {/* ---------- GRID (CARD) VIEW ---------- */}
      {view === "grid" && (
        <div className="tour-target-cookie-cards" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {displayRecords.map((r, i) => {
            const prefs = parsePrefs(r.preferences);
            const keys = Object.keys(prefs);
            const accepted = keys.filter((k) => prefs[k]).length;
            const ua = parseUA(getRecordUA(r));
            const chip = consentChip(r.consent_status);
            const state = getRowState(r);
            const ss = STATE_STYLE[state];
            const expires = getExpires(r);
            return (
              <div key={r.id || i} style={{ background: state === "expired" ? "#fef2f2" : "#fff", border: `1px solid ${state === "expired" ? "#fecaca" : C.line}`, borderRadius: 14, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <AppIcon r={r} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.app_name || "Unknown App"}</div>
                    {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: C.blue, wordBreak: "break-all" }}>{r.source_url}</a>}
                  </div>
                  <span style={{ padding: "4px 10px", borderRadius: 999, background: ss.bg, color: ss.tx, fontSize: 11.5, fontWeight: 600 }}>{ss.label}</span>
                </div>
                <div style={{ fontSize: 12, color: "#334155", display: "grid", gap: 6 }}>
                  <div><strong>IP:</strong> {getRecordIp(r) || "—"}</div>
                  <div><strong>Device:</strong> {ua.label}</div>
                  <div><strong>Date:</strong> {formatDate(parseDate(r.created_at)) || "—"}</div>
                  {expires && <div style={{ color: isExpired(r) ? C.red : C.sub }}><strong>Expires:</strong> {formatDate(expires)}</div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: chip.bg, color: chip.tx, fontWeight: 600 }}>{chip.icon} {chip.label}</span>
                    {keys.length > 0 && <span style={{ color: C.sub }}>{accepted} / {keys.length} cookies</span>}
                  </div>
                </div>
                <div style={{ marginTop: 10 }}><PrefChips prefs={r.preferences} /></div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- Pagination ---------- */}
      <CommonPagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={perPage}
        onPageChange={setPage}
        onPageSizeChange={setPerPage}
        loading={loading}
      />

      {/* ---------- Empty state ---------- */}
      {!loading && displayRecords.length === 0 && (
        <div style={{ marginTop: 40, textAlign: "center", color: C.sub, fontSize: 14 }}>
          No matching records found. Try a different search or date range.
        </div>
      )}

      {guideOpen && <GuideModal steps={GUIDE_STEPS} onClose={() => setGuideOpen(false)} />}
    </div>
  );
}

export default CookiesNotifyPage;